import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { WalletMultiButton } from "@solana/wallet-adapter-react-ui";
import { useWalletSafe } from "@/hooks/useWalletSafe";
import { useWalletSupabaseSession } from "@/hooks/useWalletSupabaseSession";
import { MobileWalletConnect } from "@/components/MobileWalletConnect";
import { NotificationBell } from "@/components/NotificationBell";
import { Admin2FAModal } from "@/components/Admin2FAModal";
import { isMobileDevice, detectMobileWallet } from "@/lib/mobileWalletFix";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { Wallet, Waves, User } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

type AppHeaderProps = {
  currentPage?: string;
};

export default function AppHeader({ currentPage = "" }: AppHeaderProps) {
  const [mobileMenu, setMobileMenu] = useState(false);
  const [showMobileWallet, setShowMobileWallet] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [hasInjectedWallet, setHasInjectedWallet] = useState(false);
  const [walletCheckDone, setWalletCheckDone] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);
  const [availableBalance, setAvailableBalance] = useState<number | null>(null);
  const navigate = useNavigate();
  const { connected, publicKey, disconnect } = useWalletSafe();
  const { status: sessionStatus, walletAddress, isAdminWallet, needs2FA, confirm2FA } = useWalletSupabaseSession();
  
  // Detect mobile and wallet on mount
  useEffect(() => {
    const mobile = isMobileDevice();
    setIsMobile(mobile);
    
    if (mobile) {
      // Check immediately
      const wallet = detectMobileWallet();
      if (wallet) {
        console.log("📱 Wallet già disponibile:", wallet);
        setHasInjectedWallet(true);
        setWalletCheckDone(true);
      } else {
        // Poll for wallet (may load after DOM)
        setTimeout(() => {
          const w = detectMobileWallet();
          if (w) {
            console.log("📱 Wallet rilevato dopo polling:", w);
            setHasInjectedWallet(true);
          }
          setWalletCheckDone(true);
        }, 500);
      }
    } else {
      setWalletCheckDone(true);
    }
  }, []);

  useEffect(() => {
    if (!connected) {
      setIsAdmin(false);
      setAvailableBalance(null);
      return;
    }

    // Wait until the wallet-auth session is synced, otherwise RLS reads can fail.
    if (sessionStatus !== "valid") {
      setIsAdmin(false);
      setAvailableBalance(null);
      return;
    }

    const checkAdminAndBalance = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      const currentUserId = session?.user?.id;
      if (!currentUserId) {
        setIsAdmin(false);
        setUserId(null);
        setAvailableBalance(null);
        return;
      }

      setUserId(currentUserId);

      // Check admin role
      const { data: roleData } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", currentUserId)
        .eq("role", "admin")
        .maybeSingle();

      setIsAdmin(!!roleData);

      // Fetch available balance
      const { data: balanceData } = await supabase
        .from("user_balances")
        .select("available")
        .eq("user_id", currentUserId)
        .maybeSingle();

      setAvailableBalance(balanceData?.available ?? 0);
    };

    checkAdminAndBalance();

    // Subscribe to balance changes
    const balanceChannel = supabase
      .channel('header-balance')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'user_balances',
      }, async () => {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user?.id) {
          const { data: balanceData } = await supabase
            .from("user_balances")
            .select("available")
            .eq("user_id", session.user.id)
            .maybeSingle();
          setAvailableBalance(balanceData?.available ?? 0);
        }
      })
      .subscribe();

    return () => {
      supabase.removeChannel(balanceChannel);
    };
  }, [connected, sessionStatus]);

  type NavItem = {
    label: string;
    path: string;
    key: string;
    requiresAuth?: boolean;
    requiresAdmin?: boolean;
  };

  const navItems: NavItem[] = [
    { label: "Home", path: "/", key: "home" },
    { label: "Marketplace", path: "/marketplace", key: "marketplace" },
    { label: "Dashboard", path: "/dashboard", key: "dashboard", requiresAuth: true },
    { label: "Analytics", path: "/analytics", key: "analytics", requiresAuth: true, requiresAdmin: true },
    { label: "Admin", path: "/admin", key: "admin", requiresAuth: true, requiresAdmin: true },
  ];

  const WalletButton = () => {
    // Don't render until wallet check is complete to prevent flickering
    if (!walletCheckDone) {
      return (
        <Button variant="outline" size="sm" className="gap-2 opacity-50" disabled>
          <Wallet className="h-4 w-4" />
          Loading...
        </Button>
      );
    }

    // Se siamo nel browser in-app del wallet, usa WalletMultiButton standard
    if (hasInjectedWallet || !isMobile) {
      return <WalletMultiButton />;
    }
    
    // Mobile senza wallet iniettato: mostra deep link options
    if (isMobile && !connected) {
      return (
        <Dialog open={showMobileWallet} onOpenChange={setShowMobileWallet}>
          <DialogTrigger asChild>
            <Button variant="hero" size="sm" className="gap-2">
              <Wallet className="h-4 w-4" />
              Connect
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-background border-border p-0">
            <MobileWalletConnect onClose={() => setShowMobileWallet(false)} />
          </DialogContent>
        </Dialog>
      );
    }
    
    return <WalletMultiButton />;
  };

  const handle2FACancel = () => {
    // Disconnect wallet if 2FA is cancelled
    disconnect?.();
  };

  return (
    <>
      {/* Admin 2FA Modal */}
      <Admin2FAModal
        isOpen={needs2FA}
        walletAddress={walletAddress || ""}
        onVerified={confirm2FA}
        onCancel={handle2FACancel}
      />

      <header className="fixed top-0 left-0 right-0 z-50 border-b border-border/50 bg-background/80 backdrop-blur-xl">
      <div className="container mx-auto px-4 py-3 flex justify-between items-center">
        <div 
          className="flex items-center gap-3 cursor-pointer group" 
          onClick={() => navigate("/")}
        >
          <div className="relative">
            <div className="absolute inset-0 bg-primary/30 rounded-xl blur-lg opacity-0 group-hover:opacity-100 transition-opacity" />
            <div className="relative w-10 h-10 gradient-primary rounded-xl flex items-center justify-center shadow-lg shadow-primary/30 group-hover:scale-110 transition-transform duration-300">
              <Waves className="w-5 h-5 text-primary-foreground" />
            </div>
          </div>
          <span className="text-xl font-bold hidden sm:inline tracking-tight">
            <span className="text-foreground">FLOW</span>{" "}
            <span className="text-muted-foreground font-medium">Marketplace</span>
          </span>
        </div>

        <div className="hidden md:flex gap-2 items-center">
          {navItems.map((item) => {
            if (item.requiresAuth && !connected) return null;
            if (item.requiresAdmin && !isAdmin) return null;
            const isActive = currentPage === item.key;
            return (
              <button
                key={item.key}
                onClick={() => navigate(item.path)}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-300 ${
                  isActive 
                    ? "bg-gradient-to-r from-primary to-accent text-white shadow-lg shadow-primary/30" 
                    : "text-muted-foreground hover:text-foreground hover:bg-primary/10 border border-transparent hover:border-primary/30"
                }`}
              >
                {item.label}
              </button>
            );
          })}
          {connected && sessionStatus === "valid" && userId && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => navigate(`/profile/${userId}`)}
                    className="p-2 rounded-lg transition-all duration-300 bg-primary/10 text-primary hover:bg-primary/20 border border-primary/30 hover:border-primary"
                  >
                    <User className="h-4 w-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>View my profile</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          {connected && sessionStatus === "valid" && <NotificationBell />}
          <WalletButton />
        </div>

        <button className="md:hidden text-foreground" onClick={() => setMobileMenu(!mobileMenu)}>
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="3" y1="12" x2="21" y2="12"></line>
            <line x1="3" y1="6" x2="21" y2="6"></line>
            <line x1="3" y1="18" x2="21" y2="18"></line>
          </svg>
        </button>
      </div>

      {mobileMenu && (
        <div className="md:hidden border-t border-border/50 bg-card/95 backdrop-blur-xl">
          <div className="container mx-auto px-4 py-4 flex flex-col gap-3">
            {navItems.map((item) => {
              if (item.requiresAuth && !connected) return null;
              if (item.requiresAdmin && !isAdmin) return null;
              const isActive = currentPage === item.key;
              return (
                <button
                  key={item.key}
                  onClick={() => {
                    navigate(item.path);
                    setMobileMenu(false);
                  }}
                  className={`w-full px-4 py-3 text-sm font-medium rounded-lg transition-all duration-300 text-left ${
                    isActive 
                      ? "bg-gradient-to-r from-primary to-accent text-white shadow-lg shadow-primary/30" 
                      : "text-muted-foreground hover:text-foreground hover:bg-primary/10 border border-border/30 hover:border-primary/30"
                  }`}
                >
                  {item.label}
                </button>
              );
            })}
            {connected && sessionStatus === "valid" && userId && (
              <button
                onClick={() => {
                  navigate(`/profile/${userId}`);
                  setMobileMenu(false);
                }}
                className="w-full px-4 py-3 text-sm font-medium rounded-lg transition-all duration-300 text-left text-muted-foreground hover:text-foreground hover:bg-primary/10 border border-border/30 hover:border-primary/30 flex items-center gap-2"
              >
                <User className="h-4 w-4 text-primary" />
                My Profile
              </button>
            )}
            <div className="flex items-center justify-between gap-3 w-full">
              {connected && sessionStatus === "valid" && <NotificationBell />}
              <div className="flex-1">
                <WalletButton />
              </div>
            </div>
          </div>
        </div>
      )}
      </header>
    </>
  );
}